import axios from 'axios';
import { apiConfig, authHeader } from '@/helpers';

// Moduł podpisywania zgód na tablecie (backend: POKO.Application/Poko/Signing).
// Wzorzec jak room.service.js: per-call baseURL + authHeader().

function opts(params) {
    return { baseURL: apiConfig.apiUrl, headers: authHeader(), params: params };
}

function get(path, params) {
    return axios.get('/api/services/app/' + path, opts(params));
}

function post(path, data, params) {
    return axios.post('/api/services/app/' + path, data, opts(params));
}

export const podpisyService = {
    // ---- terminale ----------------------------------------------------------
    getTerminals: () => get('SigningTerminal/GetAll'),
    createTerminal: (data) => post('SigningTerminal/Create', data),
    updateTerminal: (data) => post('SigningTerminal/Update', data),
    generateEnrollmentCode: (terminalId) => post('SigningTerminal/GenerateEnrollmentCode', null, { terminalId }),
    revokeCredential: (terminalId) => post('SigningTerminal/RevokeCredential', null, { terminalId }),

    // ---- Android Management API --------------------------------------------
    getEnterpriseStatus: () => get('SigningEnterprise/GetStatus'),
    createSignupUrl: (callbackUrl) => post('SigningEnterprise/CreateSignupUrl', null, { callbackUrl }),
    completeSignup: (enterpriseToken, displayName) => post('SigningEnterprise/CompleteSignup', null, { enterpriseToken, displayName }),
    attachEnterprise: (enterpriseName) => post('SigningEnterprise/AttachEnterprise', null, { enterpriseName }),
    createEnrollmentQr: (terminalId, adminPin) => post('SigningEnterprise/CreateEnrollmentQr', null, { terminalId, adminPin }),
    syncDevices: () => post('SigningEnterprise/SyncDevices'),
    issueCommand: (terminalId, command) => post('SigningEnterprise/IssueCommand', null, { terminalId, command }),
    wipeDevice: (terminalId) => post('SigningEnterprise/WipeDevice', null, { terminalId }),

    // ---- szablony -----------------------------------------------------------
    getTemplates: (includeRetired) => get('SigningTemplate/GetAll', { includeRetired: !!includeRetired }),
    getActiveTemplates: () => get('SigningTemplate/GetActive'),
    getTemplate: (id) => get('SigningTemplate/Get', { id }),
    getTemplateDocx: (id) => get('SigningTemplate/GetDocx', { id }),
    createTemplateDraft: (data) => post('SigningTemplate/CreateDraft', data),
    updateTemplateDraft: (data) => post('SigningTemplate/UpdateDraft', data),
    createNextTemplateVersion: (id, version) => post('SigningTemplate/CreateNextVersion', null, { id, version }),
    uploadTemplateDocx: (id, file) => {
        let formData = new FormData();
        formData.append('id', id);
        formData.append('file', file);
        let headers = authHeader();
        headers['Content-Type'] = 'multipart/form-data';
        return axios.post('/api/services/app/SigningTemplate/UploadDocx', formData, { baseURL: apiConfig.apiUrl, headers: headers });
    },
    analyzeTemplateDocx: (file) => {
        let formData = new FormData();
        formData.append('file', file);
        let headers = authHeader();
        headers['Content-Type'] = 'multipart/form-data';
        return axios.post('/api/services/app/SigningTemplate/AnalyzeDocx', formData, { baseURL: apiConfig.apiUrl, headers: headers });
    },
    checkTemplate: (id) => post('SigningTemplate/Check', null, { id }),
    publishTemplate: (id) => post('SigningTemplate/Publish', null, { id }),
    retireTemplate: (id) => post('SigningTemplate/Retire', null, { id }),
    deleteTemplateDraft: (id) => post('SigningTemplate/DeleteDraft', null, { id }),

    // ---- sesje --------------------------------------------------------------
    createSession: (data) => post('SigningSession/Create', data),
    cancelSession: (sessionId, reason, note) => post('SigningSession/Cancel', { sessionId, reason, note }),
    getSession: (sessionId) => get('SigningSession/Get', { sessionId }),
    getActiveSessions: () => get('SigningSession/GetActive'),
    getSessionsForClient: (clientId) => get('SigningSession/GetForClient', { clientId, maxResultCount: 50 }),

    // ---- dokumenty ----------------------------------------------------------
    getDocumentsForClient: (clientId, includeHistory) => get('SignedDocument/GetForClient', { clientId, includeHistory: !!includeHistory }),
    getDocument: (id) => get('SignedDocument/Get', { id }),
    revokeDocument: (id, note) => post('SignedDocument/Revoke', { id, note }),
};

// Etykiety statusów i kodów (spójne z backendem SigningCodes).
export const podpisyLabels = {
    sessionStatus: {
        WAITING: 'Oczekuje na tablet',
        OPENED: 'Wyświetlona na tablecie',
        CONFIRMED: 'Dane potwierdzone',
        SIGNED: 'Podpisana',
        CANCELLED: 'Anulowana',
        EXPIRED: 'Wygasła',
    },
    sessionColor: {
        WAITING: 'grey',
        OPENED: 'blue',
        CONFIRMED: 'indigo',
        SIGNED: 'green',
        CANCELLED: 'red',
        EXPIRED: 'orange',
    },
    documentStatus: {
        PENDING_RENDER: 'Trwa generowanie PDF',
        FINAL: 'Gotowy',
        RENDER_FAILED: 'Błąd generowania PDF',
    },
    documentColor: { PENDING_RENDER: 'orange', FINAL: 'green', RENDER_FAILED: 'red' },
    signerRole: {
        PATIENT: 'pacjent',
        LEGAL_REPRESENTATIVE: 'przedstawiciel ustawowy',
        DE_FACTO_GUARDIAN: 'opiekun faktyczny',
        WITNESS: 'świadek',
    },
    identity: {
        ID_CARD: 'dowód osobisty',
        PASSPORT: 'paszport',
        MOBYWATEL: 'mObywatel',
        KNOWN_PATIENT: 'pacjent znany personelowi',
        NOT_VERIFIED: 'brak weryfikacji',
    },
    cancelReason: { USER: 'Anulowanie', REFUSED: 'Odmowa pacjenta', UNABLE_TO_SIGN: 'Pacjent nie może podpisać' },
    legalForm: { NONE: 'brak wymogu', DOCUMENTARY: 'forma dokumentowa', WRITTEN: 'forma pisemna' },
    captureMethod: { CONFIRMATION: 'potwierdzenie', DRAWN_SIGNATURE: 'podpis graficzny', QUALIFIED_SIGNATURE: 'podpis kwalifikowany', IKP_OR_PERSONAL_SIGNATURE: 'IKP / podpis osobisty', PAPER: 'papier' },
    context: { RECEPTION: 'rejestracja', CONSULTATION_ROOM: 'gabinet', ANY: 'dowolny' },
    templateStatus: { DRAFT: 'szkic', PUBLISHED: 'opublikowany', RETIRED: 'wycofany' },
    event: {
        SESSION_CREATED: 'Sesja utworzona', IDENTITY_VERIFIED: 'Tożsamość zweryfikowana', SENT_TO_TERMINAL: 'Wysłano na tablet',
        DOCUMENT_DISPLAYED: 'Dokument wyświetlony', DOCUMENT_SCROLLED_TO_END: 'Przewinięto do końca', FORM_COMPLETED: 'Formularz uzupełniony',
        SUMMARY_DISPLAYED: 'Podsumowanie wyświetlone', SUMMARY_CONFIRMED: 'Dane potwierdzone', SIGNATURE_STARTED: 'Rozpoczęto podpis',
        SIGNATURE_CLEARED: 'Podpis wyczyszczony', SIGNED: 'Podpisano', SESSION_CLOSED: 'Sesja zamknięta', PDF_CREATED: 'PDF wygenerowany',
        DOCUMENT_STORED: 'Dokument zapisany', SESSION_CANCELLED: 'Sesja anulowana', SESSION_EXPIRED: 'Sesja wygasła',
        INACTIVITY_TIMEOUT: 'Brak aktywności na tablecie', APP_BACKGROUNDED: 'Aplikacja zeszła do tła', RENDER_FAILED: 'Błąd generowania PDF',
        DOCUMENT_REVOKED: 'Dokument odwołany', SIGN_REJECTED: 'Odrzucono podpis (walidacja)', SIGN_CONFLICT: 'Konflikt podpisu',
    },
};

export function abpError(e, fallback) {
    if (e && e.response && e.response.data && e.response.data.error) {
        const err = e.response.data.error;
        return err.details || err.message || fallback;
    }
    return fallback;
}
